import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Equipamento } from '../types';
import EquipamentoModal from '../components/equipamentos/EquipamentoModal';
import EquipamentoDetails from '../components/equipamentos/EquipamentoDetails';
import Modal from '../components/Modal';
import Table from '../components/ui/Table';
import equipamentosService from '../services/equipamentos';

const EquipamentosPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedEquipamento, setSelectedEquipamento] = useState<Equipamento | undefined>();

  // Debounce para pesquisa
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Query para buscar equipamentos
  const { data: equipamentosData, isLoading, refetch } = useQuery({
    queryKey: ['equipamentos', debouncedSearchTerm],
    queryFn: () => equipamentosService.list({
      search: debouncedSearchTerm || undefined,
      limit: 100
    })
  });

  const equipamentos = equipamentosData?.equipamentos || [];

  // Funções para controlar o modal
  const handleOpenModal = (equipamento?: Equipamento) => {
    setSelectedEquipamento(equipamento);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEquipamento(undefined);
  };

  const handleOpenDetailsModal = (equipamento: Equipamento) => {
    setSelectedEquipamento(equipamento);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedEquipamento(undefined);
  };

  const handleEditFromDetails = () => {
    setIsDetailsModalOpen(false);
    setIsModalOpen(true);
  };

  const handleSuccess = () => {
    refetch();
  };

  // Configuração das colunas para a tabela responsiva
  const columns = [
    {
      key: 'tipo',
      title: 'Tipo',
      mobileLabel: 'Tipo',
      render: (_: any, item: Equipamento) => item.modelo?.tipoEquipamento?.nome || '-'
    },
    {
      key: 'marca',
      title: 'Marca',
      mobileLabel: 'Marca',
      render: (_: any, item: Equipamento) => item.modelo?.marca?.nome || '-'
    },
    {
      key: 'modelo',
      title: 'Modelo',
      mobileLabel: 'Modelo',
      render: (_: any, item: Equipamento) => item.modelo?.nome || '-'
    },
    {
      key: 'numeroSerie',
      title: 'Número de Série',
      mobileLabel: 'Nº Série',
      hideMobile: true,
      render: (_: any, item: Equipamento) => item.numeroSerie || '-'
    },
    {
      key: 'cliente',
      title: 'Cliente',
      mobileLabel: 'Cliente',
      render: (_: any, item: Equipamento) => item.cliente?.nome || '-'
    },
    {
      key: 'acoes',
      title: 'Ações',
      mobileLabel: 'Ações',
      hideMobile: true,
      align: 'center' as const,
      render: (_: any, item: Equipamento) => (
        <div className="flex justify-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDetailsModal(item);
            }}
            className="text-blue-600 hover:text-blue-800 p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
            title="Visualizar"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenModal(item);
            }}
            className="text-indigo-600 hover:text-indigo-800 p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
            title="Editar"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="text-red-600 hover:text-red-800 p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
            title="Excluir"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Equipamentos</h1>
        <button
          onClick={() => handleOpenModal()}
          className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center min-h-[44px]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Equipamento
        </button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar equipamentos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px] text-base"
          />
        </div>
      </div>

      <Table
        data={equipamentos}
        columns={columns}
        keyField="id"
        isLoading={isLoading}
        emptyMessage="Nenhum equipamento encontrado"
        onRowClick={(item) => handleOpenDetailsModal(item)}
        hoverable
        striped
      />

      {/* Modal de Equipamento */}
      <EquipamentoModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        equipamento={selectedEquipamento}
        onSuccess={handleSuccess}
      />

      {/* Modal de Detalhes do Equipamento */}
      <Modal
        isOpen={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        title="Detalhes do Equipamento"
        size="xl"
      >
        {selectedEquipamento && (
          <EquipamentoDetails
            equipamento={selectedEquipamento}
            onEdit={handleEditFromDetails}
            onClose={handleCloseDetailsModal}
          />
        )}
      </Modal>
    </div>
  );
};

export default EquipamentosPage;
